import AppDialog from '../../components/AppDialog/AppDialog';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../../utils/i18n';
import { formatDateTime } from '../../utils/format';
import UserDetailModal from '../../components/UserDetailModal/UserDetailModal';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { getReports, applyModerationAction } from '../../api/authAdmin';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';

const REPORT_TABLE_SKELETON_WIDTHS = ['w-12', 'w-14', 'w-14', 'w-28', 'w-16', 'w-32', 'w-28', 'w-28', 'w-28', 'w-32'];

const PAGE_SIZE = 10;

function ClickableUserId({ userId, onOpen }) {
  if (userId == null || userId === '') {
    return <span className="text-dark-text-secondary">-</span>;
  }
  const id = Number(userId);
  if (Number.isNaN(id) || id < 1) {
    return <span className="text-dark-text-secondary">{String(userId)}</span>;
  }
  return (
    <button
      type="button"
      className="text-primary font-medium underline-offset-2 hover:underline"
      onClick={() => onOpen(id)}
    >
      {id}
    </button>
  );
}

/** Report type chip: one distinct palette per API type 1–18 (dark theme friendly). */
function getReportTypeChipClasses() {
  return 'bg-dark-surface text-dark-text-secondary border border-dark-border';
}

function getReportTypeLabel(typeValue) {
  const keyMap = {
    1: 'spam',
    2: 'nudityOrPronography',
    3: 'sexualExploitationOrSolicitation',
    4: 'sharingPrivateImages',
    5: 'involvesChild',
    6: 'hateSpeechOrSymbols',
    7: 'bullyingOrHarassment',
    8: 'falseInformationHealth',
    9: 'falseInformationPolitics',
    10: 'falseInformationSocialIssue',
    11: 'digitallyCreatedOrAltered',
    12: 'somethingElse',
    13: 'violentThreat',
    14: 'animalAbuse',
    15: 'deathOrSevereInjury',
    16: 'dangerousOrganizationsOrIndividuals',
    17: 'scamOrFraud',
    18: 'intellectualPropertyViolation',
  };
  const key = keyMap[Number(typeValue)];
  if (!key) return t('report.unknown', { value: typeValue ?? '-' });
  return t(`report.type.${key}`);
}

function getReportStatusLabel(statusValue) {
  const keyMap = {
    0: 'cancel',
    1: 'requested',
    2: 'completed',
  };
  const key = keyMap[Number(statusValue)];
  if (!key) return t('report.unknown', { value: statusValue ?? '-' });
  return t(`report.status.${key}`);
}

/** Status chip: explicit Tailwind colors per API value (0 Cancel, 1 Requested, 2 Completed). */
function getReportStatusChipClasses(statusValue) {
  const s = Number(statusValue);
  switch (s) {
    case 0:
      // Cancel — neutral slate
      return 'bg-slate-500/20 text-slate-200 border border-slate-400/40';
    case 1:
      // Requested — amber
      return 'bg-amber-500/20 text-amber-200 border border-amber-400/45';
    case 2:
      // Completed — emerald
      return 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/45';
    default:
      return 'bg-zinc-500/20 text-zinc-300 border border-zinc-500/40';
  }
}

function ReportStatusChip({ status }) {
  const label = getReportStatusLabel(status);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${getReportStatusChipClasses(status)}`}
    >
      {label}
    </span>
  );
}

function ReportTypeChip({ type }) {
  const label = getReportTypeLabel(type);
  return (
    <span
      title={label}
      className={`inline-flex max-w-[min(100%,320px)] items-center rounded-full px-2.5 py-0.5 text-xs font-medium truncate ${getReportTypeChipClasses(type)}`}
    >
      {label}
    </span>
  );
}

export default function ReportPage() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const [page, setPage] = useState(1);
  const [modalUserId, setModalUserId] = useState(null);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState('INVALID_REPORT');
  const queryClient = useQueryClient();
  const dismiss = useMutation({ mutationFn: () => applyModerationAction(dismissTarget.caseId, 'dismiss-pending', { reason_code: dismissReason.trim() }, token), onSuccess: () => { setDismissTarget(null); ['reports', 'moderation-overview', 'moderation-cases', 'moderation-users', 'moderation-user', 'moderation-user-ugc'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key, apiEnvironment] })); }, onError: (error) => { if (error.status === 409) queryClient.invalidateQueries({ queryKey: ['reports', apiEnvironment] }); } });
  useEffect(() => { setPage(1); setModalUserId(null); setDismissTarget(null); }, [apiEnvironment]);

  const reportsQuery = useQuery({
    queryKey: ['reports', apiEnvironment, page, PAGE_SIZE],
    queryFn: () => getReports(page, PAGE_SIZE, token),
    enabled: !!token,
    placeholderData: (previous, previousQuery) => previousQuery?.queryKey[1] === apiEnvironment ? previous : undefined,
  });

  useEffect(() => {
    if (reportsQuery.error?.status === 401) {
      logout();
      navigate('/login', { replace: true });
    }
  }, [reportsQuery.error, logout, navigate]);

  const {
    items: reports = [],
    total_count: totalCount = 0,
    page: currentPage = 1,
    page_size: pageSize = PAGE_SIZE,
  } = reportsQuery.data || {};

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleModalUnauthorized = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const rows = useMemo(
    () =>
      reports.map((report) => ({
        id: report.id ?? report.ID,
        userId: report.user_id ?? report.UserID,
        reportedId: report.reported_id ?? report.ReportedID,
        type: report.type ?? report.Type,
        status: report.status ?? report.Status,
        data: report.data ?? report.Data,
        createdAt: report.created_at ?? report.CreatedAt,
        completedAt: report.completed_at ?? report.CompletedAt,
        updatedAt: report.updated_at ?? report.UpdatedAt,
        caseId: report.case_id,
        caseStatus: report.case_status,
        targetType: report.target_type,
        speechId: report.speech_id ?? report.SpeechID,
        storyId: report.story_id ?? report.StoryID,
      })),
    [reports]
  );

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1920px)] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-text">{t('report.title')}</h1>
          <p className="text-dark-text-secondary text-sm mt-1">{t('report.description')}</p>
        </div>

        {reportsQuery.isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {reportsQuery.error?.message}
          </div>
        )}

        <PaginatedTableCard
          error={reportsQuery.error}
          dataUpdatedAt={reportsQuery.dataUpdatedAt}
          isPending={reportsQuery.isPending}
          isFetching={reportsQuery.isFetching}
          isEmpty={!reportsQuery.isPending && rows.length === 0}
          skeletonRows={PAGE_SIZE}
          skeletonCellWidths={REPORT_TABLE_SKELETON_WIDTHS}
          colSpan={8}
          minHeight="min-h-[28.5rem]"
          tableClassName="w-full min-w-[900px]"
          loadingLabel={t('report.table.loading')}
          emptyLabel={t('report.table.empty')}
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('report.pagination.prev')}
          nextLabel={t('report.pagination.next')}
          summary={t('report.pagination.page', { page: currentPage, totalPages })}
          header={(
            <tr className="border-b border-dark-border">
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('report.table.id')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('report.table.userId')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('report.table.reportedId')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('report.table.type')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('report.table.status')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('report.table.data')}</th>
              <th className="text-left py-3 px-4 text-dark-text-secondary font-medium text-sm">{t('report.table.createdAt')}</th><th className="py-3 px-4 text-left text-sm">Action</th>
            </tr>
          )}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-dark-border last:border-0 hover:bg-dark-surface/50">
              <td className="py-3 px-4 text-dark-text-secondary text-sm">{row.id ?? '-'}</td>
              <td className="py-3 px-4 text-sm">
                <ClickableUserId userId={row.userId} onOpen={setModalUserId} />
              </td>
              <td className="py-3 px-4 text-sm">
                <ClickableUserId userId={row.reportedId} onOpen={setModalUserId} />
              </td>
              <td className="py-3 px-4 text-sm align-middle max-w-[340px]">
                <ReportTypeChip type={row.type} />
              </td>
              <td className="py-3 px-4 text-sm align-middle">
                <ReportStatusChip status={row.status} />{row.caseId && <small className="mt-1 block text-dark-text-secondary">Case: {['Pending', 'Under review', 'Dismissed', 'Removed'][row.caseStatus] || 'Unknown'}</small>}
              </td>
              <td className="py-3 px-4 text-dark-text-secondary text-sm max-w-[300px] truncate" title={row.data ?? '-'}>
                {row.data ?? '-'}
              </td>
              <td className="py-3 px-4 text-dark-text-secondary text-sm">{formatDateTime(row.createdAt)}<details className="mt-1 text-xs"><summary>{t('adminUi.processingTimes')}</summary><p>{t('report.table.completedAt')}: {formatDateTime(row.completedAt)}</p><p>{t('report.table.updatedAt')}: {formatDateTime(row.updatedAt)}</p></details></td>
              <td className="py-3 px-4 text-sm"><div className="flex flex-col gap-2">{row.caseId && ['STORY', 'SPEECH', 'DM'].includes(row.targetType) ? <button type="button" className="rounded bg-primary px-3 py-2 text-xs" onClick={() => navigate(`/moderation?case_id=${encodeURIComponent(row.caseId)}&target_type=${encodeURIComponent(row.targetType)}`)}>Open Case #{row.caseId}</button> : <button type="button" className="text-primary underline" onClick={() => navigate(`/moderation?user_id=${row.reportedId}`)}>Open UGC history</button>}{row.targetType === 'STORY' && Number(row.caseStatus) === 0 && row.caseId && <button type="button" disabled={dismiss.isPending} className="rounded bg-emerald-700 px-3 py-2 text-xs" onClick={() => setDismissTarget(row)}>Dismiss without review</button>}</div></td>
            </tr>
          ))}
        </PaginatedTableCard>

        {dismiss.error && <p role="alert" className="mt-3 text-red-400">{dismiss.error.message}</p>}
        <AppDialog open={!!dismissTarget} mode="confirm" title="Dismiss pending Story Case" message={`Environment ${apiEnvironment.toUpperCase()} · Case #${dismissTarget?.caseId}. 연결된 모든 신고를 기각하고 판단 기록을 남깁니다. Story를 숨기지 않습니다.`} confirmLabel="Dismiss Case" onConfirm={() => { if (dismissReason.trim()) dismiss.mutate(); }} onCancel={() => setDismissTarget(null)} loading={dismiss.isPending}>
          <label className="block text-sm">기각 사유<input aria-label="기각 사유" value={dismissReason} onChange={(event) => setDismissReason(event.target.value)} className="mt-2 w-full rounded bg-dark-surface p-2" /></label>
          {!dismissReason.trim() && <p role="alert" className="text-sm text-red-400">기각 사유를 입력하세요.</p>}
        </AppDialog>
        {modalUserId != null && token && (
          <UserDetailModal
            userId={modalUserId}
            token={token}
            onClose={() => setModalUserId(null)}
            onUnauthorized={handleModalUnauthorized}
            onNavigateUserId={setModalUserId}
          />
        )}
      </div>
    </div>
  );
}
